<!-- components/courses/CourseContentManager.vue -->
<template>
  <div class="content-manager">
    <div class="content-header">
      <div class="header-left">
        <h2>Course Content</h2>
        <span class="module-count">{{ totalModules }} modules</span>
      </div>
      <div class="header-actions">
        <button class="btn-primary" @click="addSection">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add Section
        </button>
        <button class="btn-secondary" @click="reorderMode = !reorderMode">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
          {{ reorderMode ? "Done" : "Reorder" }}
        </button>
      </div>
    </div>

    <div class="sections-list">
      <div
        v-for="(section, sectionIndex) in sections"
        :key="section.id"
        class="section-item"
        :class="{ 'reorder-mode': reorderMode }"
      >
        <div class="section-header">
          <div class="section-title-wrapper">
            <span v-if="reorderMode" class="drag-handle">⠿</span>
            <span class="section-number">{{ sectionIndex + 1 }}.</span>
            <input
              v-model="section.title"
              placeholder="Section title"
              @blur="updateSection(section)"
              class="section-title-input"
            />
          </div>
          <div class="section-actions">
            <button class="btn-ghost" @click="addModule(section)">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add Module
            </button>
            <button
              class="btn-danger"
              @click="deleteSection(section.id)"
              title="Delete section"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div class="modules-list">
          <div
            v-for="(module, moduleIndex) in section.modules"
            :key="module.id"
            class="module-item"
            :class="{ 'reorder-mode': reorderMode }"
          >
            <div class="module-header">
              <div class="module-info">
                <span v-if="reorderMode" class="drag-handle">⠿</span>
                <span class="module-badge">{{
                  module.type === "video" ? "🎬" : "📄"
                }}</span>
                <input
                  v-model="module.title"
                  placeholder="Module title"
                  @blur="updateModule(section, module)"
                  class="module-title-input"
                />
              </div>
              <div class="module-actions">
                <button
                  class="btn-sm"
                  @click="editModule(section, module)"
                  :class="module.type === 'video' ? 'btn-video' : 'btn-article'"
                >
                  {{
                    module.type === "video"
                      ? "🎬 Edit Video"
                      : "✏️ Edit Article"
                  }}
                </button>
                <button
                  class="btn-sm toggle-btn"
                  @click="toggleModulePublished(section, module)"
                >
                  {{ module.isPublished ? "📌 Published" : "📝 Draft" }}
                </button>
                <button
                  class="btn-danger-sm"
                  @click="deleteModule(section.id, module.id)"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Video Content -->
            <div v-if="module.type === 'video'" class="video-content">
              <!-- If video exists, show video player with management options -->
              <div v-if="module.content?.url" class="video-preview">
                <!-- YouTube Video -->
                <div
                  v-if="module.content?.source === 'youtube'"
                  class="youtube-player"
                >
                  <iframe
                    :src="
                      module.content.embed_url ||
                      getYouTubeEmbedUrl(module.content.url)
                    "
                    frameborder="0"
                    allowfullscreen
                    allow="
                      accelerometer;
                      autoplay;
                      clipboard-write;
                      encrypted-media;
                      gyroscope;
                      picture-in-picture;
                    "
                    style="
                      width: 100%;
                      max-height: 200px;
                      border-radius: 8px;
                      aspect-ratio: 16/9;
                    "
                  ></iframe>
                  <div class="video-meta">
                    <div class="video-info">
                      <span
                        >▶️ {{ module.content.name || "YouTube Video" }}</span
                      >
                      <span>📊 YouTube</span>
                      <span>⏱️ {{ module.content.duration || "N/A" }}</span>
                    </div>
                    <div class="video-actions">
                      <button
                        class="btn-sm btn-view"
                        @click="viewVideo(module)"
                        title="View video"
                      >
                        👁️ View
                      </button>
                      <button
                        class="btn-sm btn-replace"
                        @click="editModule(section, module)"
                        title="Replace video"
                      >
                        🔄 Replace
                      </button>
                      <button
                        class="btn-sm btn-danger"
                        @click="removeVideo(section, module)"
                        title="Delete video"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Uploaded Video -->
                <div v-else>
                  <video
                    :src="module.content.url"
                    controls
                    style="
                      max-width: 100%;
                      max-height: 200px;
                      border-radius: 8px;
                    "
                  />
                  <div class="video-meta">
                    <div class="video-info">
                      <span>📁 {{ module.content.name || "Video" }}</span>
                      <span>📊 {{ formatFileSize(module.content.size) }}</span>
                      <span
                        >⏱️
                        {{
                          module.content.duration || "Duration unknown"
                        }}</span
                      >
                    </div>
                    <div class="video-actions">
                      <button
                        class="btn-sm btn-view"
                        @click="viewVideo(module)"
                        title="View video"
                      >
                        👁️ View
                      </button>
                      <button
                        class="btn-sm btn-replace"
                        @click="editModule(section, module)"
                        title="Replace video"
                      >
                        🔄 Replace
                      </button>
                      <button
                        class="btn-sm btn-danger"
                        @click="removeVideo(section, module)"
                        title="Delete video"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- If no video, show upload button -->
              <div
                v-else
                class="upload-area"
                @click="editModule(section, module)"
              >
                <div class="upload-placeholder">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <p>Click to upload video or add YouTube link</p>
                  <span class="file-limits"
                    >Max 100MB • MP4, MOV, AVI, WebM • YouTube</span
                  >
                </div>
              </div>
            </div>

            <!-- Article Content -->
            <div v-if="module.type === 'article'" class="article-content">
              <div v-if="module.content?.html" class="article-preview">
                <div
                  class="article-snippet"
                  v-html="getArticleSnippet(module.content.html)"
                ></div>
                <button
                  class="btn-sm btn-article"
                  @click="editModule(section, module)"
                >
                  ✏️ Edit Article
                </button>
              </div>
              <div
                v-else
                class="empty-article"
                @click="editModule(section, module)"
              >
                <p>📝 Click to write your article</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Module Editor Modal -->
    <div
      v-if="editingModule"
      class="module-editor-modal"
      @click.self="closeEditor"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h3>
            {{
              editingModule.type === "video"
                ? "🎬 Upload Video or Add YouTube Link"
                : "📝 Write Article"
            }}
          </h3>
          <button class="close-btn" @click="closeEditor">×</button>
        </div>
        <div class="modal-body">
          <!-- Video Editor -->
          <VideoUploader
            v-if="editingModule.type === 'video'"
            :module="editingModule"
            :section="editingSection"
            :courseId="course.id"
            @uploaded="handleVideoUploaded"
            @cancel="closeEditor"
          />
          <!-- Article Editor -->
          <ArticleEditor
            v-else
            :module="editingModule"
            :section="editingSection"
            @saved="handleArticleSaved"
            @cancel="closeEditor"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useCourseStore } from "@/stores/course";
import VideoUploader from "./VideoUploader.vue";
import ArticleEditor from "./ArticleEditor.vue";
import { uploadVideoFile, deleteFile } from "@/services/cloudinary";
import api from "@/services/api";

const props = defineProps({
  course: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["update"]);

const courseStore = useCourseStore();
const sections = ref(props.course.sections || []);
const editingModule = ref(null);
const editingSection = ref(null);
const reorderMode = ref(false);
const videoInputs = ref(null);

const totalModules = computed(() => {
  return sections.value.reduce(
    (acc, section) => acc + section.modules.length,
    0,
  );
});

// Watch for course updates
watch(
  () => props.course,
  (newCourse) => {
    sections.value = newCourse.sections || [];
  },
  { deep: true },
);

// Section Management
const addSection = async () => {
  const result = await courseStore.addSection(props.course.id, {
    title: `Section ${sections.value.length + 1}`,
    order: sections.value.length,
  });
  if (result.success) {
    sections.value.push(result.section);
    emit("update");
  }
};

const updateSection = async (section) => {
  const updatedSections = sections.value.map((s) =>
    s.id === section.id ? section : s,
  );
  await courseStore.updateCourse(props.course.id, {
    sections: updatedSections,
  });
  emit("update");
};

const deleteSection = async (sectionId) => {
  if (!confirm("Delete this section and all its modules?")) return;
  const updatedSections = sections.value.filter((s) => s.id !== sectionId);
  await courseStore.updateCourse(props.course.id, {
    sections: updatedSections,
  });
  sections.value = updatedSections;
  emit("update");
};

// Module Management
const addModule = async (section) => {
  const type = confirm(
    "Add a video module? Click OK for Video, Cancel for Article",
  )
    ? "video"
    : "article";
  const result = await courseStore.addModule(props.course.id, section.id, {
    title: `${type === "video" ? "Video" : "Article"} ${section.modules.length + 1}`,
    type: type,
    order: section.modules.length,
    content: type === "video" ? {} : { html: "" },
    isPublished: false,
  });
  if (result.success) {
    const sectionIndex = sections.value.findIndex((s) => s.id === section.id);
    sections.value[sectionIndex].modules.push(result.module);
    emit("update");
  }
};

const updateModule = async (section, module) => {
  const sectionIndex = sections.value.findIndex((s) => s.id === section.id);
  const moduleIndex = sections.value[sectionIndex].modules.findIndex(
    (m) => m.id === module.id,
  );
  sections.value[sectionIndex].modules[moduleIndex] = module;

  const updatedSections = sections.value.map((s) =>
    s.id === section.id
      ? {
          ...s,
          modules: s.modules.map((m) => (m.id === module.id ? module : m)),
        }
      : s,
  );
  await courseStore.updateCourse(props.course.id, {
    sections: updatedSections,
  });
  emit("update");
};

const deleteModule = async (sectionId, moduleId) => {
  if (!confirm("Delete this module?")) return;
  const sectionIndex = sections.value.findIndex((s) => s.id === sectionId);
  sections.value[sectionIndex].modules = sections.value[
    sectionIndex
  ].modules.filter((m) => m.id !== moduleId);
  await courseStore.updateCourse(props.course.id, { sections: sections.value });
  emit("update");
};

const toggleModulePublished = async (section, module) => {
  module.isPublished = !module.isPublished;
  await updateModule(section, module);
};

// Module Editing
const editModule = (section, module) => {
  editingModule.value = module;
  editingSection.value = section;
};

const closeEditor = () => {
  editingModule.value = null;
  editingSection.value = null;
};

const handleVideoUploaded = async (data) => {
  const sectionIndex = sections.value.findIndex(
    (s) => s.id === editingSection.value.id,
  );
  const moduleIndex = sections.value[sectionIndex].modules.findIndex(
    (m) => m.id === editingModule.value.id,
  );

  // Handle YouTube videos differently
  if (data.source === "youtube") {
    sections.value[sectionIndex].modules[moduleIndex].content = {
      url: data.url,
      embed_url: data.embed_url,
      video_id: data.video_id,
      source: "youtube",
      name: data.name || "YouTube Video",
      type: "youtube",
      duration: data.duration || "N/A",
      size: 0,
    };
  } else {
    // Regular uploaded video
    sections.value[sectionIndex].modules[moduleIndex].content = {
      url: data.url,
      public_id: data.public_id,
      streaming_url: data.streaming_url || data.url,
      duration: data.duration || "0:00",
      size: data.size,
      name: data.name || "Video",
      type: data.type || "video/mp4",
      source: "upload",
    };
  }

  sections.value[sectionIndex].modules[moduleIndex].isPublished = true;

  await courseStore.updateCourse(props.course.id, { sections: sections.value });
  emit("update");
  closeEditor();
};

const getYouTubeEmbedUrl = (url) => {
  const videoId = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/,
  )?.[1];
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

const handleArticleSaved = async (content) => {
  const sectionIndex = sections.value.findIndex(
    (s) => s.id === editingSection.value.id,
  );
  const moduleIndex = sections.value[sectionIndex].modules.findIndex(
    (m) => m.id === editingModule.value.id,
  );

  sections.value[sectionIndex].modules[moduleIndex].content = { html: content };
  sections.value[sectionIndex].modules[moduleIndex].isPublished = true;

  await courseStore.updateCourse(props.course.id, { sections: sections.value });
  emit("update");
  closeEditor();
};

// View video in new tab
const viewVideo = (module) => {
  if (module.content?.url) {
    window.open(module.content.url, "_blank");
  }
};

// Remove video
const removeVideo = async (section, module) => {
  if (!confirm("Remove this video?")) return;

  // Delete from Cloudinary only if it was uploaded
  if (module.content?.source === "upload" && module.content?.public_id) {
    await deleteFile(module.content.public_id, "video");
  }

  module.content = {};
  await updateModule(section, module);
};

const getArticleSnippet = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = div.textContent || "";
  return text.substring(0, 150) + (text.length > 150 ? "..." : "");
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;
$primary-blue: #001829;

.content-manager {
  height: 100%;
  overflow-y: auto;

  .content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;

      h2 {
        margin: 0;
        font-size: 24px;
        color: #1a1a1a;
      }

      .module-count {
        font-size: 14px;
        color: #868e96;
        background: #f1f3f5;
        padding: 4px 12px;
        border-radius: 12px;
      }
    }

    .header-actions {
      display: flex;
      gap: 8px;

      .btn-primary {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 16px;
        background: $primary-orange;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: darken($primary-orange, 10%);
        }
      }

      .btn-secondary {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 16px;
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 8px;
        font-weight: 600;
        color: #495057;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: #f1f3f5;
        }
      }
    }
  }

  .video-meta {
    margin-top: 8px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    font-size: 12px;
    color: #868e96;
    flex-wrap: wrap;
    padding: 8px;
    background: #f8f9fa;
    border-radius: 8px;

    .video-info {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      flex: 1;
    }

    .video-actions {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;

      .btn-sm {
        padding: 4px 12px;
        border: none;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;

        &.btn-view {
          background: rgba(0, 123, 255, 0.1);
          color: #007bff;

          &:hover {
            background: rgba(0, 123, 255, 0.2);
          }
        }

        &.btn-replace {
          background: rgba(255, 128, 0, 0.1);
          color: $primary-orange;

          &:hover {
            background: rgba(255, 128, 0, 0.2);
          }
        }

        &.btn-danger {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;

          &:hover {
            background: rgba(220, 53, 69, 0.2);
          }
        }
      }
    }
  }

  .youtube-player {
    iframe {
      width: 100%;
      max-height: 200px;
      border-radius: 8px;
      aspect-ratio: 16/9;
    }
  }

  .video-preview {
    video {
      width: 100%;
      max-height: 200px;
      border-radius: 8px;
    }
  }

  .sections-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .section-item {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #e9ecef;

    &.reorder-mode {
      border-color: $primary-orange;
      background: rgba(255, 128, 0, 0.05);
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .section-title-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1;

        .drag-handle {
          font-size: 20px;
          color: #adb5bd;
          cursor: grab;
          user-select: none;
        }

        .section-number {
          font-weight: 700;
          color: $primary-orange;
        }

        .section-title-input {
          background: transparent;
          border: none;
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          padding: 4px 8px;
          border-radius: 4px;
          width: 300px;

          &:hover {
            background: #fff;
          }

          &:focus {
            background: #fff;
            outline: 2px solid $primary-orange;
          }
        }
      }

      .section-actions {
        display: flex;
        gap: 8px;

        .btn-ghost {
          background: none;
          border: none;
          color: $primary-orange;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          transition: all 0.2s ease;

          &:hover {
            background: rgba(255, 128, 0, 0.1);
          }
        }

        .btn-danger {
          background: none;
          border: none;
          color: #dc3545;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: all 0.2s ease;

          &:hover {
            background: rgba(220, 53, 69, 0.1);
          }
        }
      }
    }

    .modules-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding-left: 32px;

      .module-item {
        background: #fff;
        border-radius: 8px;
        padding: 12px 16px;
        border: 1px solid #e9ecef;

        &.reorder-mode {
          border-color: $primary-orange;
        }

        .module-header {
          display: flex;
          justify-content: space-between;
          align-items: center;

          .module-info {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;

            .drag-handle {
              font-size: 16px;
              color: #adb5bd;
              cursor: grab;
              user-select: none;
            }

            .module-badge {
              font-size: 18px;
            }

            .module-title-input {
              background: transparent;
              border: none;
              font-size: 14px;
              font-weight: 500;
              color: #1a1a1a;
              padding: 4px 8px;
              border-radius: 4px;
              flex: 1;
              max-width: 400px;

              &:hover {
                background: #f8f9fa;
              }

              &:focus {
                background: #fff;
                outline: 2px solid $primary-orange;
              }
            }
          }

          .module-actions {
            display: flex;
            gap: 6px;

            .btn-sm {
              padding: 4px 12px;
              border: none;
              border-radius: 4px;
              font-size: 12px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s ease;

              &.btn-video {
                background: rgba(40, 167, 69, 0.1);
                color: #28a745;

                &:hover {
                  background: rgba(40, 167, 69, 0.2);
                }
              }

              &.btn-article {
                background: rgba(0, 123, 255, 0.1);
                color: #007bff;

                &:hover {
                  background: rgba(0, 123, 255, 0.2);
                }
              }

              &.toggle-btn {
                background: #f8f9fa;
                color: #495057;

                &:hover {
                  background: #f1f3f5;
                }
              }
            }

            .btn-danger-sm {
              background: none;
              border: none;
              color: #dc3545;
              cursor: pointer;
              padding: 4px;
              border-radius: 4px;

              &:hover {
                background: rgba(220, 53, 69, 0.1);
              }
            }
          }
        }

        .video-content,
        .article-content {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #f1f3f5;

          .video-preview {
            video {
              width: 100%;
              max-height: 200px;
              border-radius: 8px;
            }

            .video-meta {
              margin-top: 8px;
              display: flex;
              gap: 16px;
              font-size: 12px;
              color: #868e96;
              align-items: center;
              flex-wrap: wrap;

              .btn-danger-sm {
                background: none;
                border: none;
                color: #dc3545;
                cursor: pointer;
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 600;

                &:hover {
                  background: rgba(220, 53, 69, 0.1);
                }
              }
            }
          }

          .upload-area {
            border: 2px dashed #dee2e6;
            border-radius: 8px;
            padding: 24px;
            text-align: center;
            cursor: pointer;
            transition: all 0.3s ease;

            &:hover {
              border-color: $primary-orange;
              background: rgba(255, 128, 0, 0.05);
            }

            .upload-placeholder {
              svg {
                color: #adb5bd;
                margin-bottom: 8px;
              }

              p {
                margin: 0 0 4px 0;
                color: #1a1a1a;
                font-weight: 500;
              }

              .file-limits {
                font-size: 12px;
                color: #868e96;
              }
            }
          }

          .article-preview {
            .article-snippet {
              color: #495057;
              line-height: 1.6;
              font-size: 14px;
              margin-bottom: 8px;
            }
          }

          .empty-article {
            text-align: center;
            padding: 24px;
            background: #f8f9fa;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.3s ease;

            &:hover {
              background: #e9ecef;
            }

            p {
              margin: 0;
              color: #868e96;
            }
          }
        }
      }
    }
  }
}

.module-editor-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;

  .modal-content {
    background: #fff;
    border-radius: 16px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      border-bottom: 1px solid #e9ecef;

      h3 {
        margin: 0;
        color: #1a1a1a;
      }

      .close-btn {
        background: none;
        border: none;
        font-size: 28px;
        color: #868e96;
        cursor: pointer;
        padding: 0 8px;

        &:hover {
          color: #1a1a1a;
        }
      }
    }

    .modal-body {
      padding: 24px;
      max-height: calc(90vh - 80px);
      overflow-y: auto;
    }
  }
}
</style>
